import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Shield, 
  Zap, 
  Scale, 
  Globe, 
  ArrowRight,
  CheckCircle2,
  Upload,
  PenTool,
  Phone,
  Mail,
  MapPin,
  Clock,
  Users,
  Award,
  BookOpen,
  Play,
  Send,
  Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Contact Form Component
const ContactForm = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setIsSubmitted(true);
      setFormData({ first_name: '', last_name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-slate-200 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Message Sent!
          </h3>
          <p className="text-slate-600 mb-6">
            Thank you for reaching out. Our team will review your message and get back to you shortly.
          </p>
          <Button 
            onClick={() => setIsSubmitted(false)}
            variant="outline"
          >
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 shadow-lg">
      <CardContent className="p-8">
        <h3 className="text-xl font-semibold text-[#0F172A] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Send Us a Message
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">First Name *</label>
              <input 
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-colors"
                placeholder="John"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Last Name</label>
              <input 
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-colors"
                placeholder="Doe"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Email *</label>
            <input 
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-colors"
              placeholder="john@company.com"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Subject</label>
            <input 
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-colors"
              placeholder="How can we help?"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Message *</label>
            <textarea 
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-colors resize-none"
              placeholder="Tell us about your requirements..."
              required
            ></textarea>
          </div>
          <Button 
            type="submit"
            className="w-full btn-primary py-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner w-4 h-4 mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const LandingPage = () => {
  const { login, isAuthenticated, user } = useAuth();
  
  const features = [
    {
      icon: FileText,
      title: "Upload Your Notice",
      description: "Simply upload your legal notice - whether it's a GST Show Cause Notice, Customs demand, FEMA penalty order, or PMLA notice. We handle it all."
    },
    {
      icon: Shield,
      title: "Expert Review",
      description: "Our team of experienced legal professionals and retired departmental officers personally reviews every notice you submit."
    },
    {
      icon: Scale,
      title: "Case Law Research",
      description: "We research relevant case laws, precedents, and legal provisions to build a strong foundation for your response."
    },
    {
      icon: Zap,
      title: "Strategic Advisory",
      description: "Receive clear, actionable recommendations on next steps - whether to contest, negotiate, or comply - based on merit analysis."
    },
    {
      icon: Globe,
      title: "Draft Assistance",
      description: "We help draft professional replies, representations, and appeals tailored to your specific case and the issuing authority."
    },
    {
      icon: Users,
      title: "Personal Attention",
      description: "Led by Ashok Kumar Singh, Sole Proprietor, every case receives personal attention from experienced leadership."
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Upload Your Notice",
      description: "Sign in with Google and upload your legal notice PDF. Our system extracts all text automatically.",
      icon: Upload
    },
    {
      step: "02",
      title: "Expert Review",
      description: "Our legal team reviews your notice, identifies key issues, deadlines, and potential risks.",
      icon: Search
    },
    {
      step: "03",
      title: "We Contact You",
      description: "Our team reaches out with a detailed advisory on next steps and recommended course of action.",
      icon: PenTool
    },
    {
      step: "04",
      title: "Draft & Submit",
      description: "We help you draft and finalize your reply or representation, ready for submission to authorities.",
      icon: CheckCircle2
    }
  ];

  const expertiseAreas = [
    { name: "Indirect Tax (GST)", description: "Show cause notices, demands, appeals, refunds" },
    { name: "Customs & Import-Export", description: "Duty disputes, classification, valuation issues" },
    { name: "FEMA Compliance", description: "Foreign exchange violations, compounding applications" },
    { name: "PMLA Regulations", description: "Anti-money laundering notices, attachment orders" }
  ];

  const stats = [
    { number: "Expert", label: "Legal Team Review" },
    { number: "Personal", label: "Attention to Every Case" },
    { number: "24-48hrs", label: "Initial Response Time" },
    { number: "Secure", label: "Document Handling" }
  ];

  const teamMembers = [
    {
      name: "Expert Legal Team",
      role: "Tax Law Specialists",
      description: "Our team combines young legal talent with experienced retired departmental officers."
    }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                <img src="/aks-logo.png" alt="AKS Tax & Legal Consultancy" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-[#1E3A8A] leading-tight">
                  AKS TAX AND LEGAL<br/>CONSULTANCY
                </h1>
              </div>
            </div>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('expertise')}
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors"
              >
                Expertise
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors"
              >
                Contact
              </button>
            </div>

            <div className="flex items-center gap-4">
              {isAuthenticated && user ? (
                <Button 
                  onClick={() => window.location.href = (user.role === 'admin' || user.role === 'super_admin') ? '/admin' : '/dashboard'}
                  className="btn-primary"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={login}
                  className="btn-primary"
                  data-testid="login-btn"
                >
                  Sign in with Google
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 hero-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-[#1E3A8A]/5 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Scale className="w-4 h-4" />
                Expert Legal Advisory for Tax Notices
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Received a Tax Notice?{' '}
                <span className="text-gradient-quantum">We Can Help.</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                Upload your GST, Customs, FEMA, or PMLA notice and let our expert team of legal professionals 
                and retired departmental officers guide you through the best course of action.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-4 mb-10">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <p className="text-2xl font-bold text-[#1E3A8A]">{stat.number}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={login}
                  className="btn-accent text-lg px-8 py-6"
                  data-testid="get-started-btn"
                >
                  Upload Your Notice
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-lg px-8 py-6 border-slate-300 hover:bg-slate-50"
                  onClick={() => scrollToSection('how-it-works')}
                  data-testid="learn-more-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  How It Works
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/10 to-[#F59E0B]/10 rounded-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGRvY3VtZW50cyUyMG9mZmljZXxlbnwwfHx8fDE3NzI1MzQxMDB8MA&ixlib=rb-4.1.0&q=85"
                alt="Legal Documents"
                className="relative z-10 rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg z-20 animate-fade-in">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Expert Review</p>
                    <p className="text-sm text-slate-500">Within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1758518730523-c9f6336ebdae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxsZWdhbCUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3MjUzMDE0M3ww&ixlib=rb-4.1.0&q=85"
                alt="Legal Team Meeting"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">About AKS Tax & Legal Consultancy</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                A New Era in Legal Tech
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                <strong>AKS Tax & Legal Consultancy</strong> is an advanced techno-legal tax advisory and solutions firm designed for the next era of professional services. We are not a traditional law office — we are a modern, technology-driven knowledge bank that integrates deep domain expertise with AI-powered tools.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our team combines young, dynamic legal professionals with experienced retired departmental officers who bring decades of practical knowledge. This unique combination enables us to provide a faster, smarter, and more future-ready approach to techno-legal tax consulting.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Expert Team</h4>
                    <p className="text-sm text-slate-500">Retired officers & legal experts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">AI-Powered</h4>
                    <p className="text-sm text-slate-500">Quantum-level accuracy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 gradient-legal-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Platform Features</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mt-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Intelligent Legal Workflow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive platform designed for modern legal teams handling complex regulatory matters.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card key={idx} className="card-legal p-8 hover:border-[#1E3A8A]/30 bg-white" data-testid={`feature-card-${idx}`}>
                <CardContent className="p-0">
                  <div className="w-14 h-14 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-[#1E3A8A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Simple Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mt-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              How We Help You
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Upload your notice and let our expert team handle the rest. We'll guide you through every step.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, idx) => (
              <div key={idx} className="relative">
                {idx < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-[#1E3A8A] to-transparent z-0" style={{ width: 'calc(100% - 2rem)' }}></div>
                )}
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className="text-5xl font-bold text-[#1E3A8A]/10">{item.step}</span>
                  <h3 className="text-xl font-semibold text-[#0F172A] mt-2 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Button 
              onClick={login}
              className="btn-primary text-lg px-10 py-6"
              data-testid="try-now-btn"
            >
              Try It Now - It's Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Expertise Areas Section */}
      <section id="expertise" className="py-20 px-6 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Areas of Specialization
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Deep expertise in key regulatory domains affecting businesses in India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertiseAreas.map((area, idx) => (
              <div 
                key={idx}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors group"
              >
                <div className="w-12 h-12 bg-[#F59E0B] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {area.name}
                </h3>
                <p className="text-slate-400 text-sm">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial/CTA Section */}
      <section className="py-20 px-6 gradient-legal-paper">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-white border border-slate-200 mx-auto shadow-lg">
            <img src="/aks-logo.png" alt="AKS" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mt-8 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Transform Your Legal Workflow?
          </h2>
          <p className="text-lg text-slate-600 mb-4">
            Join leading legal teams who trust AKS Tax & Legal Consultancy for their regulatory compliance needs.
          </p>
          <p className="text-slate-500 mb-8">
            Free to get started. No credit card required. Admin approval ensures secure access.
          </p>
          <Button 
            onClick={login}
            className="btn-primary text-lg px-10 py-6"
            data-testid="cta-sign-in-btn"
          >
            Sign in with Google
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Contact Us
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Have questions about our platform or need assistance with your legal tax matters? 
                Our team is here to help you navigate complex regulatory challenges.
              </p>
              
              {/* Leadership Section */}
              <div className="bg-[#FDFBF7] rounded-xl p-6 mb-8 border border-slate-100">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1E3A8A]" />
                  Leadership
                </h4>
                <div className="grid gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1E3A8A] rounded-full flex items-center justify-center text-white font-bold">
                      AK
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Ashok Kumar Singh</p>
                      <p className="text-sm text-slate-500">Sole Proprietor</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Office Address</h4>
                    <p className="text-slate-600">
                      House No-648, C/o Arun Kumar Singh,<br />
                      Street No-39, Smriti Nagar, Bhilai,<br />
                      Durg, Chhattisgarh - 490020, India
                    </p>
                    <p className="text-xs text-slate-400 mt-1">GSTIN: 22ANOPS4530N1ZK</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Email</h4>
                    <p className="text-slate-600">Contact us for email details</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Phone</h4>
                    <p className="text-slate-600">
                      Available upon request
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Business Hours</h4>
                    <p className="text-slate-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex-shrink-0">
                  <img src="/aks-logo.png" alt="AKS" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">AKS Tax & Legal Consultancy</h3>
                  <p className="text-sm text-slate-400">Sole Proprietorship of Ashok Kumar Singh</p>
                </div>
              </div>
              <p className="text-slate-400 mb-4 max-w-md">
                An advanced techno-legal tax advisory firm combining deep domain expertise with AI-powered tools. 
                Transforming legal workflows with intelligent document processing.
              </p>
              <p className="text-sm text-slate-500">
                GSTIN: 22ANOPS4530N1ZK
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-white transition-colors">How It Works</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Expertise</h4>
              <ul className="space-y-2 text-slate-400">
                <li>Indirect Tax (GST)</li>
                <li>Customs & Import-Export</li>
                <li>FEMA Compliance</li>
                <li>PMLA Regulations</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              &copy; {new Date().getFullYear()} AKS Tax & Legal Consultancy. All rights reserved.
            </p>
            <div className="flex gap-6 text-slate-400 text-sm">
              <button className="hover:text-white transition-colors">Privacy Policy</button>
              <button className="hover:text-white transition-colors">Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
